import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFitStore } from '../store/useFitStore';
import type { Session, Client } from '../store/types';
import SessionEditor, { defaultSections } from '../components/coach/SessionEditor';
import ApplyProgramModal from '../components/coach/ApplyProgramModal';
import ClientHub from '../components/coach/ClientHub';
import ImportClients from '../components/coach/ImportClients';
import FirstRunBoard from '../components/coach/FirstRunBoard';
import { billingBadge, billingToneColor } from '../lib/billing';

/* ---------- date helpers (local-time, no UTC drift) ---------- */
const startOfWeek = (d: Date): Date => {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // Monday = 0
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
};
const addDays = (d: Date, n: number): Date => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const isoLocal = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const fmtShort = (d: Date): string => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

type WeeksToShow = 1 | 2 | 4;

// Module-scope so its type identity is stable across renders (an in-body component
// would remount every roster row's dot on each render).
const StatusDot: React.FC<{ s: 'none' | 'partial' | 'done' }> = ({ s }) => (
  <span
    title={s === 'done' ? 'On track' : s === 'partial' ? 'In progress' : 'Not programmed'}
    style={{
      width: 9,
      height: 9,
      borderRadius: '50%',
      background: s === 'done' ? 'rgba(255,255,255,0.9)' : s === 'partial' ? 'rgba(255,255,255,0.45)' : 'transparent',
      border: s === 'none' ? '1.5px solid rgba(255,255,255,0.3)' : 'none',
      flexShrink: 0,
    }}
  />
);

const WeeklyBoard: React.FC = () => {
  const { user, clients, sessions, checkIns, clientBilling, dataLoaded, showToast, upsertSession, saveWeekAsProgram } = useFitStore();

  // Board view-state is restored from sessionStorage so a refresh lands the coach
  // exactly where they were — same client, same week, same span.
  const [selectedClientId, setSelectedClientId] = useState<string>(() => sessionStorage.getItem('fitflex_board_client') || clients[0]?.id || '');
  const [search, setSearch] = useState('');
  const [weekOffset, setWeekOffset] = useState<number>(() => Number(sessionStorage.getItem('fitflex_board_weekOffset') || '0'));
  const [weeksToShow, setWeeksToShow] = useState<WeeksToShow>(() => {
    const n = Number(sessionStorage.getItem('fitflex_board_span'));
    return n === 1 || n === 2 || n === 4 ? n : 2;
  });
  const [editor, setEditor] = useState<{ session: Session; isNew: boolean } | null>(null);
  const [mobileShowCalendar, setMobileShowCalendar] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [hubOpen, setHubOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Select the first client once the roster loads, or if a restored selection no
  // longer exists (e.g. that client was removed since last visit).
  useEffect(() => {
    if (clients.length > 0 && !clients.some((c) => c.id === selectedClientId)) {
      setSelectedClientId(clients[0].id);
    }
  }, [clients, selectedClientId]);

  // Persist view-state for refresh restoration.
  useEffect(() => { if (selectedClientId) sessionStorage.setItem('fitflex_board_client', selectedClientId); }, [selectedClientId]);
  useEffect(() => { sessionStorage.setItem('fitflex_board_weekOffset', String(weekOffset)); }, [weekOffset]);
  useEffect(() => { sessionStorage.setItem('fitflex_board_span', String(weeksToShow)); }, [weeksToShow]);

  // Deep links: /coach?client=<id>&hub=1 preselects a client and opens their hub;
  // /coach?import=1 opens the import wizard (Today's empty state). Params are
  // cleared after handling so refresh is clean.
  useEffect(() => {
    const cid = searchParams.get('client');
    const wantsImport = searchParams.get('import');
    if (cid && clients.some((c) => c.id === cid)) {
      setSelectedClientId(cid);
      setMobileShowCalendar(true);
      if (searchParams.get('hub')) setHubOpen(true);
    }
    if (wantsImport) setImportOpen(true);
    if (cid || wantsImport) setSearchParams({}, { replace: true });
  }, [searchParams, clients, setSearchParams]);

  const today = new Date();
  const todayIso = isoLocal(today);
  // todayIso in the deps so the grid re-anchors when the calendar day changes
  // (e.g. the board left open across midnight), keeping it in sync with the
  // per-render weekStart/currentMonday math below.
  const baseMonday = useMemo(() => addDays(startOfWeek(today), weekOffset * 7), [weekOffset, todayIso]); // eslint-disable-line react-hooks/exhaustive-deps

  // Days to render across the visible weeks
  const days = useMemo(
    () => Array.from({ length: weeksToShow * 7 }, (_, i) => addDays(baseMonday, i)),
    [baseMonday, weeksToShow]
  );
  const rangeLabel = `${fmtShort(days[0])} – ${fmtShort(days[days.length - 1])}`;

  // Sessions for the selected client, indexed by date
  const sessionsByDate = useMemo(() => {
    const map: Record<string, Session[]> = {};
    sessions
      .filter((s) => s.clientId === selectedClientId)
      .forEach((s) => {
        (map[s.date] ||= []).push(s);
      });
    return map;
  }, [sessions, selectedClientId]);

  // Per-client status for THIS week (the first visible week), for the list badges.
  const weekStart = isoLocal(addDays(startOfWeek(today), weekOffset * 7));
  const weekEnd = isoLocal(addDays(startOfWeek(today), weekOffset * 7 + 6));
  const currentMonday = isoLocal(startOfWeek(today));

  // Single-pass indexes so the roster render is O(sessions)+O(checkIns) total
  // rather than a full scan per client row.
  const statusByClient = useMemo(() => {
    const acc: Record<string, { total: number; done: number }> = {};
    sessions.forEach((s) => {
      if (s.date < weekStart || s.date > weekEnd) return;
      const e = (acc[s.clientId] ||= { total: 0, done: 0 });
      e.total++;
      if (s.status === 'completed') e.done++;
    });
    return acc;
  }, [sessions, weekStart, weekEnd]);
  const clientStatus = (clientId: string): 'none' | 'partial' | 'done' => {
    const e = statusByClient[clientId];
    if (!e || e.total === 0) return 'none';
    return e.done === e.total ? 'done' : 'partial';
  };

  // Check-in "due" = no check-in for the ACTUAL current week (independent of the
  // week being viewed). Set of clients who DO have one → O(1) lookup per row.
  const checkedInThisWeek = useMemo(
    () => new Set(checkIns.filter((c) => c.weekOf === currentMonday).map((c) => c.clientId)),
    [checkIns, currentMonday]
  );
  const checkInDue = (clientId: string): boolean => !checkedInThisWeek.has(clientId);

  const filteredClients = useMemo(() => {
    const q = search.toLowerCase();
    return clients.filter((c) => c.name.toLowerCase().includes(q));
  }, [clients, search]);
  const selectedClient = clients.find((c) => c.id === selectedClientId);

  // First-visible-week dates already programmed for the selected client — used by
  // the Apply-program clash guard. Memoized so it isn't rebuilt on every render.
  const occupiedDates = useMemo(
    () => new Set(sessions.filter((s) => s.clientId === selectedClientId && s.date >= weekStart && s.date <= weekEnd).map((s) => s.date)),
    [sessions, selectedClientId, weekStart, weekEnd]
  );

  const pickClient = (id: string) => {
    setSelectedClientId(id);
    setMobileShowCalendar(true);
  };

  /* ---------- create / edit sessions ---------- */
  const openNewSession = (dateIso: string) => {
    if (!selectedClientId || !user) return;
    setEditor({
      isNew: true,
      session: {
        id: crypto.randomUUID(),
        clientId: selectedClientId,
        coachId: user.id,
        date: dateIso,
        title: '',
        status: 'planned',
        sections: defaultSections(),
      },
    });
  };

  /* ---------- the scale levers ---------- */
  const copyLastWeek = () => {
    // Source = the week before the first visible week; target = the first visible week.
    const sourceStart = isoLocal(addDays(baseMonday, -7));
    const sourceEnd = isoLocal(addDays(baseMonday, -1));
    const source = sessions.filter(
      (s) => s.clientId === selectedClientId && s.date >= sourceStart && s.date <= sourceEnd
    );
    if (source.length === 0) {
      showToast('Nothing programmed last week to copy', 'error');
      return;
    }
    const occupied = new Set(
      sessions
        .filter((s) => s.clientId === selectedClientId && s.date >= weekStart && s.date <= weekEnd)
        .map((s) => s.date)
    );
    let copied = 0;
    source.forEach((s) => {
      const target = new Date(s.date + 'T00:00:00');
      target.setDate(target.getDate() + 7);
      const targetIso = isoLocal(target);
      if (occupied.has(targetIso)) return; // never clobber a day that's already programmed
      upsertSession({
        ...s,
        id: crypto.randomUUID(),
        date: targetIso,
        status: 'planned',
        sections: s.sections.map((sec) => ({
          ...sec,
          id: `${sec.id}_c${copied}`,
          exercises: sec.exercises.map((ex) => ({ ...ex, id: `${ex.id}_c${copied}` })),
        })),
      });
      copied++;
    });
    showToast(
      copied === 0
        ? 'Week already programmed — nothing copied'
        : `Copied ${copied} session${copied === 1 ? '' : 's'} from last week — tweak the numbers`
    );
  };

  const handleSaveAsProgram = () => {
    const count = sessions.filter(
      (s) => s.clientId === selectedClientId && s.date >= weekStart && s.date <= weekEnd
    ).length;
    if (count === 0) {
      showToast('No sessions in this week to save', 'error');
      return;
    }
    const label = `${selectedClient?.name || 'Client'} — Week of ${fmtShort(new Date(weekStart + 'T00:00:00'))}`;
    saveWeekAsProgram(selectedClientId, weekStart, label);
    showToast(`Saved "${label}" to the Library`);
  };

  // First run: an empty roster gets a single welcome card instead of a blank
  // week grid and client-dependent buttons that do nothing. dataLoaded gates it
  // so a coach WITH clients never sees this flash while the fetch is in flight.
  if (dataLoaded && clients.length === 0) {
    return (
      <>
        <div className="w-full max-w-[1440px] mx-auto px-4 md:px-8 py-6 flex flex-col" style={{ minHeight: '70vh' }}>
          <FirstRunBoard name={user?.name || 'Coach'} onAdd={() => setImportOpen(true)} />
        </div>
        {importOpen && <ImportClients onClose={() => setImportOpen(false)} />}
      </>
    );
  }

  return (
    <>
    <div className="w-full max-w-[1440px] mx-auto px-4 md:px-8 py-6">
      {/* overflow-clip, NOT hidden: hidden makes this a containing scroll box
          and silently kills the sticky toolbar inside <main> */}
      <div className="flex gap-0 md:gap-6 rounded-fit-lg overflow-clip">
        {/* ---------- CLIENT LIST ---------- */}
        <aside
          data-tour="roster"
          className={`${mobileShowCalendar ? 'hidden' : 'flex'} md:flex flex-col w-full md:w-[300px] md:shrink-0`}
        >
          <div className="mb-4">
            <p className="mono-label">Clients</p>
            <h1 className="text-[26px] font-bold tracking-tight leading-tight">All Clients</h1>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search client…"
            className="input mb-2"
          />
          <button onClick={() => setImportOpen(true)} className="btn btn-glass btn-sm w-full mb-3">
            ＋ Add clients
          </button>
          <div className="flex flex-col gap-1 overflow-y-auto no-scrollbar" style={{ maxHeight: '70vh' }}>
            {filteredClients.map((c: Client) => {
              const active = c.id === selectedClientId;
              const st = clientStatus(c.id);
              const initials = c.name.split(' ').map((w) => w[0]).slice(0, 2).join('');
              const badge = billingBadge(clientBilling.find((b) => b.clientId === c.id) || null);
              return (
                <button
                  key={c.id}
                  onClick={() => pickClient(c.id)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-fit-md text-left transition-colors ${
                    active ? 'bg-white/10' : 'hover:bg-white/5'
                  }`}
                >
                  <span className="w-8 h-8 rounded-full bg-fit-bg-tertiary flex items-center justify-center text-[11px] font-bold shrink-0">
                    {initials}
                  </span>
                  <span className="flex-1 text-[14px] font-medium truncate">{c.name}</span>
                  {badge && badge.tone !== 'muted' && (
                    <span
                      title={`Billing: ${badge.label}`}
                      className="text-[8px] uppercase tracking-[0.08em] border rounded px-1 py-0.5 leading-none shrink-0"
                      style={{ color: billingToneColor(badge.tone), borderColor: 'var(--line-strong)' }}
                    >
                      {badge.label}
                    </span>
                  )}
                  {checkInDue(c.id) && (
                    <span
                      title="Check-in due"
                      className="text-[8px] uppercase tracking-[0.08em] text-fit-text-secondary border border-fit-line-strong rounded px-1 py-0.5 leading-none shrink-0"
                    >
                      due
                    </span>
                  )}
                  <StatusDot s={st} />
                </button>
              );
            })}
          </div>
        </aside>

        {/* ---------- CALENDAR ---------- */}
        <main
          className={`${mobileShowCalendar ? 'flex' : 'hidden'} md:flex flex-col flex-1 min-w-0`}
        >
          {/* Toolbar + week nav — pinned under the app header so the client
              switcher and date controls never scroll out of reach */}
          <div
            className="sticky z-[90]"
            style={{
              top: 'var(--header-height)',
              marginLeft: 'calc(var(--spacing-md) * -1)',
              marginRight: 'calc(var(--spacing-md) * -1)',
              padding: '10px var(--spacing-md) 0',
              background: 'rgba(0,0,0,0.85)',
              backdropFilter: 'saturate(180%) blur(20px)',
              WebkitBackdropFilter: 'saturate(180%) blur(20px)',
            }}
          >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileShowCalendar(false)}
                className="md:hidden text-fit-text-secondary text-sm"
              >
                ← Clients
              </button>
              <button
                data-tour="client-hub"
                onClick={() => selectedClient && setHubOpen(true)}
                disabled={!selectedClient}
                className="flex items-center gap-3 rounded-fit-md border border-fit-line hover:border-fit-line-strong hover:bg-white/5 px-2.5 py-1.5 transition-colors disabled:cursor-default text-left"
                title="Open client profile, history & check-in"
              >
                <span className="w-8 h-8 rounded-full bg-fit-bg-tertiary flex items-center justify-center text-[11px] font-bold">
                  {selectedClient?.name.split(' ').map((w) => w[0]).slice(0, 2).join('') || '—'}
                </span>
                <div>
                  <h2 className="text-[18px] font-bold tracking-tight leading-none">{selectedClient?.name || 'No client'}</h2>
                  {selectedClient && (
                    <p className="text-[11px] text-fit-text-secondary mt-1 flex items-center gap-1">
                      View profile · history · check-in <span aria-hidden>›</span>
                    </p>
                  )}
                </div>
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button className="btn btn-glass btn-sm" onClick={() => setApplyOpen(true)}>
                Apply program ↓
              </button>
              <button className="btn btn-glass btn-sm" onClick={copyLastWeek}>
                Copy last week ⟳
              </button>
              <button className="btn btn-glass btn-sm" onClick={handleSaveAsProgram}>
                Save as Program
              </button>
            </div>
          </div>

          {/* Date nav + view toggle */}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-fit-line">
            <div className="flex items-center gap-2">
              <button className="btn btn-outline btn-sm" onClick={() => setWeekOffset(0)}>Today</button>
              <button className="px-2 py-1 rounded hover:bg-white/5 text-fit-text-secondary" onClick={() => setWeekOffset((w) => w - 1)} aria-label="Previous">‹</button>
              <span className="text-sm font-semibold tabular-nums min-w-[120px] text-center">{rangeLabel}</span>
              <button className="px-2 py-1 rounded hover:bg-white/5 text-fit-text-secondary" onClick={() => setWeekOffset((w) => w + 1)} aria-label="Next">›</button>
            </div>
            <div className="flex items-center gap-1 bg-fit-bg-tertiary rounded-full p-1">
              {([1, 2, 4] as WeeksToShow[]).map((w) => (
                <button
                  key={w}
                  onClick={() => setWeeksToShow(w)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                    weeksToShow === w ? 'bg-white text-fit-ink' : 'text-fit-text-secondary hover:text-white'
                  }`}
                >
                  {w} Week
                </button>
              ))}
            </div>
          </div>
          </div>

          <div className="h-4" aria-hidden />

          {/* Weekday header (desktop only — tablets keep the agenda list; a 7-col
              grid under ~1024px squeezes session titles into unreadable stubs) */}
          <div className="hidden lg:grid grid-cols-7 gap-2 mb-2">
            {WEEKDAYS.map((d) => (
              <div key={d} className="mono-label text-center">{d}</div>
            ))}
          </div>

          {/* Day grid — 7 cols on desktop, stacked agenda on mobile */}
          <div data-tour="week-grid" className="grid grid-cols-1 lg:grid-cols-7 gap-2">
            {days.map((d) => {
              const key = isoLocal(d);
              const daySessions = sessionsByDate[key] || [];
              const isToday = key === todayIso;
              return (
                <div
                  key={key}
                  className={`rounded-fit-md border p-2 md:min-h-[150px] flex flex-col gap-1.5 ${
                    isToday ? 'border-fit-line-strong bg-white/[0.03]' : 'border-fit-line'
                  }`}
                >
                  <div className="flex items-center gap-2 md:block">
                    <span className="lg:hidden mono-label">{WEEKDAYS[(d.getDay() + 6) % 7]}</span>
                    <span
                      className={`inline-flex items-center justify-center text-[12px] font-semibold ${
                        isToday ? 'bg-white text-fit-ink rounded-full w-6 h-6' : 'text-fit-text-secondary'
                      }`}
                    >
                      {d.getDate()}
                    </span>
                  </div>

                  {daySessions.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setEditor({ session: s, isNew: false })}
                      className="text-left rounded-md bg-fit-bg-tertiary hover:bg-fit-bg-elevated border border-fit-line px-2 py-1.5 transition-colors"
                    >
                      <div className="flex items-center gap-1.5">
                        <span
                          style={{
                            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                            background: s.status === 'completed' ? 'rgba(255,255,255,0.9)' : s.status === 'partial' ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.25)',
                          }}
                        />
                        <span className="text-[12px] font-semibold truncate">{s.title}</span>
                      </div>
                      <span className="text-[10px] text-fit-muted">
                        {(() => {
                          const n = s.sections.reduce((acc, sec) => acc + sec.exercises.length, 0);
                          return `${n} exercise${n === 1 ? '' : 's'}`;
                        })()}
                      </span>
                    </button>
                  ))}

                  <button
                    onClick={() => openNewSession(key)}
                    className="mt-auto text-fit-muted/60 hover:text-fit-text-secondary text-[18px] leading-none self-start px-1"
                    aria-label="Add session"
                  >
                    +
                  </button>
                </div>
              );
            })}
          </div>

          {/* week-at-a-glance for the first visible week — fills the dead zone
              under the grid with the numbers a coach actually scans for */}
          {selectedClient && (
            <p className="mono-label mt-4">
              {(() => {
                const e = statusByClient[selectedClient.id];
                const total = e?.total || 0;
                const done = e?.done || 0;
                const parts = [
                  total === 0 ? 'Nothing programmed this week' : `${total} session${total === 1 ? '' : 's'} programmed · ${done} completed`,
                  checkInDue(selectedClient.id) ? 'check-in due' : 'checked in ✓',
                ];
                return `${selectedClient.name.split(' ')[0]} · ${parts.join(' · ')}`;
              })()}
            </p>
          )}
        </main>
      </div>

      {/* ---------- session editor (create + edit) ---------- */}
      {editor && (
        <SessionEditor
          session={editor.session}
          isNew={editor.isNew}
          clientName={selectedClient?.name || 'Client'}
          onClose={() => setEditor(null)}
        />
      )}

      {/* ---------- per-client hub (CRM / history / check-in) ---------- */}
      {hubOpen && selectedClient && (
        <ClientHub key={selectedClient.id} client={selectedClient} onClose={() => setHubOpen(false)} />
      )}



      {/* ---------- apply a Library program to this week ---------- */}
      {applyOpen && selectedClient && user && (
        <ApplyProgramModal
          clientId={selectedClient.id}
          clientName={selectedClient.name}
          coachId={user.id}
          weekDays={days.slice(0, 7)}
          occupiedDates={occupiedDates}
          isoLocal={isoLocal}
          onClose={() => setApplyOpen(false)}
        />
      )}
    </div>
    {/* outside the branch divs so the wizard survives the first-run -> board flip */}
    {importOpen && <ImportClients onClose={() => setImportOpen(false)} />}
    </>
  );
};

export default WeeklyBoard;
